import _ from 'lodash'
import { useMemo } from 'react'

import { useGetLicensesQuery } from '@/core/services/license'
import { useGetTimeTrackingConsentQuery } from '@/core/api'
import { Role } from '@/core/types/shared'
import { SidebarItem, SidebarType } from '@/utils/subscription/types'
import { usePendingOrdersBadge } from '@/hooks/usePendingOrdersBadge'
import { useAppSelector } from '@/redux/rootReducer'
import { useElaraChatEnabled } from '@/providers/elara/hooks/useElaraChatEnabled'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { sidebarItemsMap } from './sidebarItems'

/**
 * Builds the sidebar entries of the connected user from the `sidebar:*` components of their
 * licenses, plus the entries granted by other means (Elara service, tutorials, time tracking).
 */
export function useSidebarItems() {
  const authUser = useAppSelector(selectAuthUser)
  const { data: licenses, isLoading: isLoadingLicenses } = useGetLicensesQuery({ id: authUser?.licenses }, { skip: !authUser?.licenses.length })
  const { data: timeTrackingConsent } = useGetTimeTrackingConsentQuery(undefined, { skip: !authUser })
  const pendingOrdersBadge = usePendingOrdersBadge()

  const canUseElaraChat = useElaraChatEnabled()

  const sidebarItems: SidebarItem[] = useMemo(() => {
    const allowedSidebarComponents = _.uniq(_.flatten(licenses?.map((license) => license.components)))
      .filter((component) => component.startsWith('sidebar:'))
      .map((component) => component.replace('sidebar:', '')) as SidebarType[]

    // Show the unified Elara agent sidebar entry whenever the ELARA chat is available (licence or
    // rollout flag), even if the license component list doesn't explicitly include sidebar:medconnect-agent.
    if (canUseElaraChat && !allowedSidebarComponents.includes(SidebarType.MedConnectAgent)) {
      allowedSidebarComponents.push(SidebarType.MedConnectAgent)
    }

    // Tutorials is help content — always visible to every authenticated user,
    // regardless of license components.
    if (!allowedSidebarComponents.includes(SidebarType.Tutorials)) {
      allowedSidebarComponents.push(SidebarType.Tutorials)
    }

    const items = _.compact(allowedSidebarComponents.map((component) => sidebarItemsMap[component]).flat())

    const isAdmin = authUser?.roles?.includes(Role.Admin) ?? false
    const showTimeTracking = isAdmin || timeTrackingConsent?.enabled

    if (showTimeTracking) {
      const timeTrackingItem = sidebarItemsMap[SidebarType.TimeTracking]
      if (timeTrackingItem && !Array.isArray(timeTrackingItem)) {
        items.push({ ...timeTrackingItem, link: isAdmin ? '/admin/time-tracking' : '/time-tracking' })
      }
    }

    // INS admin entries come from the INS_MANAGEMENT license components; trusted actors stays
    // hidden until that page ships.
    return items
      .filter((item) => item.name !== SidebarType.AdminTrustedActors)
      .map((item) => (item.name === SidebarType.MeetingLinks ? { ...item, badge: pendingOrdersBadge } : item))
  }, [licenses, canUseElaraChat, pendingOrdersBadge, timeTrackingConsent, authUser])

  return { sidebarItems, isLoadingLicenses, canUseElaraChat }
}
